"use client";

/**
 * Turns a 404 on a concept URL into a request we can act on.
 *
 * It used to record the miss silently. It now also *asks*: someone who typed a
 * concept URL we have not written is the single best-qualified person to tell
 * us it is worth writing, and a passive event captures that intent at its
 * weakest. One button converts an ambient signal into a deliberate one.
 *
 * ## The promise is review, not publication
 *
 * The confirmation says a human will consider it. That wording is load-bearing
 * in two directions. It is honest, because most requests will not be written.
 * And it protects the corpus: pages auto-generated from search misses are
 * precisely what search engines demote as thin programmatic content, and they
 * would put the reviewed pages at risk by association. Nothing a visitor types
 * can put a page on the site — the terminal state of this pipeline is a ranked
 * list a person reads. Never soften "we will consider" into "we will add".
 *
 * The abuse gate is unchanged and still runs first: a term the screen rejects
 * gets no invitation at all, so there is no button to press and nothing to
 * record. That is deliberate. An off-domain string should not be offered a
 * channel into the backlog, and should not be told it was refused either.
 *
 * Renders inside the not-found page because the attempted slug lives in the
 * URL, and reading `location` needs a client component; confining it to one
 * keeps the rest of that page static.
 *
 * It sits on the app's root not-found page rather than a `/learn`-specific one.
 * That is not where it belongs conceptually, and it is where it has to be: a
 * segment-level not-found only renders with `dynamicParams = true`, which makes
 * Vercel serve unknown slugs as 200. A soft-404 costs more than the tidier file
 * layout is worth, so this filters by path instead.
 *
 * The slug is converted back to a phrase ("cap-table" → "cap table") before
 * screening, because the screen scores natural-language terms. Everything else
 * — the abuse gate, the silent drop for rejected terms, the reason it is safe
 * to carry a screened term at all — is shared with `LearnSearch`; see
 * `screenTermProposal` and `trackLearnTermMissed`.
 *
 * A direct-hit 404 is weaker signal than a search miss (it can be a stale link
 * or a typo as easily as an ask), which is what `surface` distinguishes.
 */
import { useEffect, useRef, useState } from "react";

import { trackLearnTermMissed } from "@/lib/analytics/client";
import { screenTermProposal } from "@/lib/learn/termProposal";
import { notifyTermRequest } from "@/lib/learn/termRequestClient";

/** "cap-table" → "cap table". Slugs are the only shape that reaches here. */
export function slugToPhrase(slug: string): string {
  return slug.replace(/[-_]+/g, " ").trim();
}

/**
 * The attempted concept slug, or "" when this is not a concept URL at all.
 *
 * Path-aware because this now runs on the app's ROOT not-found page, which
 * catches every 404 in the app. Without the `/learn/` check, a mistyped
 * `/orbit` or a stale marketing URL would be screened as a concept proposal and
 * could land in the content backlog — quietly polluting the one dataset that is
 * supposed to be founders asking about startup vocabulary.
 */
export function attemptedSlugFromPath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length !== 2 || segments[0] !== "learn") return "";
  return segments[1] ?? "";
}

/** A screened, on-domain term worth offering to record. */
interface Candidate {
  term: string;
  score: number;
}

export function LearnMissCapture() {
  const recordedRef = useRef(false);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (recordedRef.current) return;
    recordedRef.current = true;

    const slug = attemptedSlugFromPath(window.location.pathname);
    if (!slug) return;

    const verdict = screenTermProposal(slugToPhrase(slug));
    // Rejected terms get no event and no invitation. The visitor is not told
    // they were screened — an abuse gate that announces itself is a gate
    // somebody probes.
    if (verdict.status !== "candidate") return;

    trackLearnTermMissed({
      screenedTerm: verdict.normalized,
      score: verdict.score,
      surface: "learn-not-found",
    });
    setCandidate({ term: verdict.normalized, score: verdict.score });
  }, []);

  if (!candidate) return null;

  if (requested) {
    return (
      <div
        // Announced so the confirmation reaches a screen reader: the button
        // that produced it is gone, so nothing else would signal the change.
        role="status"
        className="mx-auto mb-9 max-w-[46ch] rounded-2xl border border-lev-orange/30 bg-lev-orange-50/60 px-6 py-5"
      >
        <p className="text-[15px] leading-[1.6] text-landing-dark">
          Noted, thank you. A human reviews every suggestion and decides what
          gets written next, so we cannot promise this one will appear. Enough
          people asking is genuinely how the list gets ordered.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto mb-9 max-w-[46ch] rounded-2xl border border-slate-300 bg-white px-6 py-5">
      <p className="mb-4 text-[15px] leading-[1.6] text-landing-secondary">
        We have not written{" "}
        <span className="font-semibold text-landing-dark">{candidate.term}</span>{" "}
        yet. Should we?
      </p>
      <button
        type="button"
        onClick={() => {
          // Optimistic by design: the founder gets their acknowledgement
          // immediately and a dropped notification costs us one data point,
          // never their confirmation. The server emits `learn_term_requested`
          // alongside the email — one emitter at the callable boundary.
          setRequested(true);
          notifyTermRequest(candidate.term, "learn-not-found");
        }}
        className="rounded-full bg-landing-dark px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-landing-dark/90"
      >
        Ask us to write it
      </button>
    </div>
  );
}
